import { useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostRentalIncomesDashboardMutation } from '@client/api';
import { Card, CardContent, CardHeader, CardTitle, InfoTooltip, Skeleton, toCurrencyString, CurrencyCode } from '@client/toolkit';
import { FaRegCircleQuestion } from 'react-icons/fa6';
import { BarChart } from '../../../dashboard/components';
import { BarTooltipProps, BarDatum } from '@nivo/bar';
import dayjs from 'dayjs';
import 'dayjs/locale/nb';

interface CompanyRentalIncomeWidgetProps {
  companyId: string;
}

export function CompanyRentalIncomeWidget({ companyId }: CompanyRentalIncomeWidgetProps) {
  const { t, i18n } = useTranslation('widget');
  const { t: tCompanies } = useTranslation('companies');

  const now = dayjs();
  const currentYear = now.year();
  const currentMonth = now.month() + 1; // 1-indexed
  const startDate = `${currentYear}-01-01`;
  const endDate = `${currentYear}-12-31`;

  const [trigger, { data, isLoading }] = usePostRentalIncomesDashboardMutation();

  useEffect(() => {
    trigger({
      startDate,
      endDate,
      companyId,
      body: { buildingIds: [] },
    });
  }, [companyId, startDate, endDate, trigger]);

  const monthlyIncomesMemoized = useMemo(() => {
    const futureFlags = Array.from({ length: 12 }, (_, index) => index + 1 > currentMonth);

    const monthlyIncomes = Array.from({ length: 12 }, (_, index) => ({
      month: index + 1,
      income: 0,
      futureIncome: 0,
      commonAreaCosts: 0,
      futureCommonAreaCosts: 0,
    }));

    data?.monthlyIncomes?.forEach((monthlyIncome) => {
      const monthIndex = monthlyIncome.month - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        if (futureFlags[monthIndex]) {
          monthlyIncomes[monthIndex].futureIncome = monthlyIncome.income.amount;
        } else {
          monthlyIncomes[monthIndex].income = monthlyIncome.income.amount;
        }
      }
    });

    data?.monthlyCommonAreaCosts?.forEach((monthlyCommonArea) => {
      const monthIndex = monthlyCommonArea.month - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        if (futureFlags[monthIndex]) {
          monthlyIncomes[monthIndex].futureCommonAreaCosts = monthlyCommonArea.income.amount;
        } else {
          monthlyIncomes[monthIndex].commonAreaCosts = monthlyCommonArea.income.amount;
        }
      }
    });

    return monthlyIncomes;
  }, [data?.monthlyIncomes, data?.monthlyCommonAreaCosts, currentMonth]);

  const totalAmount = (data?.totalRentalIncome?.amount ?? 0) + (data?.totalCommonAreaCosts?.amount ?? 0);
  const currencyCode = data?.totalRentalIncome?.currency?.code as CurrencyCode;

  const dayjsLocale = i18n.language === 'no' ? 'nb' : 'en';

  const formatMonth = useCallback(
    (month: number) => {
      return dayjs().locale(dayjsLocale).month(month - 1).format('MMM');
    },
    [dayjsLocale],
  );

  const CustomTooltip = useCallback(
    ({ id, value, color }: BarTooltipProps<BarDatum>) => {
      const isRental = id === 'income' || id === 'futureIncome';
      const label = isRental ? t('totalIncome.rentalIncome') : t('totalIncome.commonAreaCosts');
      const baseColor = isRental ? '#3F98B4' : '#8DD4E8';
      return (
        <div className="bg-gray-800 text-white px-3 py-2 rounded shadow-lg text-sm">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: baseColor }} />
            <span className="font-medium">{label}:</span>
            <span>
              {toCurrencyString(value, undefined, {
                currency: currencyCode,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </span>
          </div>
        </div>
      );
    },
    [t, currencyCode],
  );

  const loading = !data || isLoading;

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle variant="default" className="flex items-center gap-2">
          <span>{t('totalIncome.title')}</span>
          <InfoTooltip message={tCompanies('rentalIncomeWidget.tooltip')} messageClassName="w-56 z-50" enterDelay="delay-100">
            <FaRegCircleQuestion className="text-gray-400 text-sm" />
          </InfoTooltip>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="w-full min-h-[250px]" />
        ) : (
          <>
            <div className="flex gap-2 mb-4">
              <span className="text-white text-4xl font-bold">
                {toCurrencyString(totalAmount, undefined, {
                  currency: currencyCode,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </span>
            </div>
            <div className="w-full h-[200px]">
              <BarChart
                data={monthlyIncomesMemoized}
                indexBy="month"
                keys={['income', 'futureIncome', 'commonAreaCosts', 'futureCommonAreaCosts']}
                colors={({ id }) => {
                  switch (id) {
                    case 'income': return '#3F98B4';
                    case 'futureIncome': return '#3F98B488';
                    case 'commonAreaCosts': return '#8DD4E8';
                    case 'futureCommonAreaCosts': return '#6BC5DC88';
                    default: return '#3F98B4';
                  }
                }}
                groupMode="grouped"
                tooltip={CustomTooltip}
                axisBottom={{
                  tickSize: 0,
                  tickPadding: 10,
                  format: (v) => formatMonth(Number(v)),
                }}
                defs={[
                  {
                    id: 'future-lines',
                    type: 'patternLines',
                    spacing: 4,
                    rotation: -45,
                    lineWidth: 1,
                    background: 'inherit',
                    color: 'rgba(0, 0, 0, 0.3)',
                  },
                ]}
                fill={[
                  { match: { id: 'futureIncome' }, id: 'future-lines' },
                  { match: { id: 'futureCommonAreaCosts' }, id: 'future-lines' },
                ]}
                legends={[
                  {
                    dataFrom: 'keys',
                    anchor: 'bottom',
                    direction: 'row',
                    translateX: 0,
                    translateY: 50,
                    itemWidth: 120,
                    itemHeight: 16,
                    data: [
                      {
                        color: '#3F98B4',
                        id: 'income',
                        label: t('totalIncome.rentalIncome'),
                      },
                      {
                        color: '#8DD4E8',
                        id: 'commonAreaCosts',
                        label: t('totalIncome.commonAreaCosts'),
                      },
                      {
                        color: '#3F98B488',
                        id: 'futureIncome',
                        label: t('totalIncome.futureIncome'),
                      },
                    ],
                  },
                ]}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
